<script lang="ts">
  // ============================================================
  // Modern Dashboard - AX Hub Admin
  // ------------------------------------------------------------
  // Enhanced with professional design system and UX patterns
  // ============================================================
  import { page } from '@inertiajs/svelte';
  import { auth } from '@/stores/user';

  let {
    stats = {
      posts_count: 0,
      posts_change: null,
      comments_count: 0,
      comments_change: null,
      likes_count: 0,
      likes_change: null,
      notifications_count: 0,
      notifications_change: null,
    }
  }: {
    stats?: {
      posts_count: number;
      posts_change: number | null;
      comments_count: number;
      comments_change: number | null;
      likes_count: number;
      likes_change: number | null;
      notifications_count: number;
      notifications_change: number | null;
    };
  } = $props();

  const MIN_CHANGE_THRESHOLD = 5; // 5% 미만 변동은 표시 안 함

  function shouldShowChange(change: number | null): boolean {
    return change !== null && Math.abs(change) >= MIN_CHANGE_THRESHOLD;
  }

  let error: string | null = $state(null);
  let showAvatarModal = $state(false);
  let selectedAvatarColor = $state('primary');
  let uploadedImage: string | null = $state(null);
  let devAutoLoggedIn = $derived(Boolean((page.props as any)?.auth?.dev_auto_logged_in));
  let loginPath = $derived(((page.props as any)?.auth?.login_path as string) || '/login');
  let logoutPath = $derived(((page.props as any)?.auth?.logout_path as string) || '/logout');

  const statCards = [
    {
      label: '게시글',
      key: 'posts_count' as const,
      changeKey: 'posts_change' as const,
      icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
      color: 'primary',
      bgClass: 'bg-primary-50 dark:bg-primary/15',
      iconClass: 'text-primary',
      gradient: 'from-primary-500 to-primary-600',
    },
    {
      label: '댓글',
      key: 'comments_count' as const,
      changeKey: 'comments_change' as const,
      icon: 'M7 8h10M7 12h4m1 8l-4-2H5a2 2 0 01-2-2V8a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2l-4 2z',
      color: 'success',
      bgClass: 'bg-success-light dark:bg-success/15',
      iconClass: 'text-success',
      gradient: 'from-emerald-500 to-green-600',
    },
    {
      label: '좋아요',
      key: 'likes_count' as const,
      changeKey: 'likes_change' as const,
      icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
      color: 'warning',
      bgClass: 'bg-warning-light dark:bg-warning/15',
      iconClass: 'text-warning',
      gradient: 'from-amber-500 to-orange-600',
    },
    {
      label: '알림',
      key: 'notifications_count' as const,
      changeKey: 'notifications_change' as const,
      icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
      color: 'info',
      bgClass: 'bg-info-light dark:bg-info/15',
      iconClass: 'text-info',
      gradient: 'from-blue-500 to-cyan-600',
    },
  ];

  const quickActions = [
    {
      label: '새 게시글 작성',
      icon: 'M12 4v16m8-8H4',
      href: '/boards',
      color: 'primary',
    },
    {
      label: '메시지',
      icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
      href: '/messages',
      color: 'info',
    },
    {
      label: '알림',
      icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
      href: '/notifications',
      color: 'warning',
    },
    {
      label: '설정',
      icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
      href: '/settings',
      color: 'gray',
    },
  ];

  const avatarColors = [
    { name: 'primary', gradient: 'from-primary-400 to-primary-600', label: '파란색' },
    { name: 'success', gradient: 'from-emerald-400 to-green-600', label: '초록색' },
    { name: 'warning', gradient: 'from-amber-400 to-orange-600', label: '주황색' },
    { name: 'error', gradient: 'from-rose-400 to-red-600', label: '빨간색' },
    { name: 'purple', gradient: 'from-purple-400 to-violet-600', label: '보라색' },
    { name: 'pink', gradient: 'from-pink-400 to-rose-600', label: '핑크색' },
  ];

  function openAvatarModal() {
    showAvatarModal = true;
  }

  function closeAvatarModal() {
    showAvatarModal = false;
  }

  function handleFileUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        uploadedImage = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  function saveAvatar() {
    // TODO: API 호출로 아바타 저장
    console.log('아바타 저장:', { color: selectedAvatarColor, image: uploadedImage });
    closeAvatarModal();
  }

  function handleBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      closeAvatarModal();
    }
  }
</script>

<div class="space-y-6">

  <!-- Header Section -->
  <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
    <div class="space-y-1">
      <h1 class="text-h1 text-content">
        내 대시보드
      </h1>
      <p class="text-body2 text-content-secondary">
        안녕하세요, <span class="font-semibold text-content">{$auth.user?.name || $auth.user?.nickname || $auth.user?.email?.split('@')[0] || ''}</span>님
      </p>
    </div>
  </div>

  {#if devAutoLoggedIn}
    <section class="rounded-3xl border border-warning/25 bg-gradient-to-r from-warning-light via-amber-50 to-white p-5 shadow-sm dark:border-warning/20 dark:bg-warning/10">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div class="space-y-1.5">
          <div class="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-warning-dark dark:bg-dark-secondary/60 dark:text-warning">
            <span class="inline-block h-2 w-2 rounded-full bg-warning"></span>
            개발 모드 자동 로그인
          </div>
          <p class="text-base font-semibold text-content">
            이 화면은 실제 세션 로그인 없이 개발용 계정으로 자동 진입한 상태입니다.
          </p>
          <p class="text-sm text-content-secondary">
            로그인과 로그아웃 플로우를 점검하려면 실제 로그인 화면으로 이동해서 테스트하는 편이 명확합니다.
          </p>
        </div>
        <div class="flex flex-wrap gap-3">
          <a href={loginPath} class="inline-flex items-center justify-center rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100">
            실제 로그인 화면 보기
          </a>
          <a href="/boards" class="inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 dark:border-dark-border dark:bg-dark-secondary dark:text-dark-text-high dark:hover:bg-dark-tertiary">
            커뮤니티 둘러보기
          </a>
        </div>
      </div>
    </section>
  {:else}
    <section class="rounded-3xl border border-success/20 bg-gradient-to-r from-success-light/70 to-white p-5 shadow-sm dark:border-success/20 dark:bg-success/10">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div class="space-y-1.5">
          <div class="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-success dark:bg-dark-secondary/60">
            <span class="inline-block h-2 w-2 rounded-full bg-success"></span>
            로그인됨
          </div>
          <p class="text-base font-semibold text-content">
            현재 계정으로 대시보드에 로그인되어 있습니다.
          </p>
          <p class="text-sm text-content-secondary">
            여기서 내 활동 요약을 보고, 게시판 작성이나 메시지, 설정으로 바로 이동할 수 있습니다.
          </p>
        </div>
        <div class="flex flex-wrap gap-3">
          <a href="/settings" class="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-dark">
            설정 열기
          </a>
          <a href={logoutPath} class="inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 dark:border-dark-border dark:bg-dark-secondary dark:text-dark-text-high dark:hover:bg-dark-tertiary">
            로그아웃
          </a>
        </div>
      </div>
    </section>
  {/if}

    {#if error}
      <div
        class="bg-error-light dark:bg-error/10 border border-error/20 dark:border-error/30 rounded-xl p-4 animate-slide-down"
        role="alert"
      >
        <div class="flex items-start gap-3">
          <svg class="w-5 h-5 text-error mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-error">마이페이지 로딩 오류</p>
            <p class="text-sm text-error/80 mt-1 break-words">{error}</p>
          </div>
        </div>
      </div>
    {/if}

    <div class="grid lg:grid-cols-3 gap-6">
      <!-- Left Column: Profile & Quick Actions -->
      <div class="lg:col-span-1 space-y-6">

        <!-- Profile Card -->
        <div class="card card-hoverable p-6">
          <div class="flex items-center gap-4 mb-6">
            <div class="relative">
              <button
                onclick={openAvatarModal}
                class="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg shrink-0 cursor-pointer hover:scale-105 transition-all duration-200 group relative overflow-hidden"
                aria-label="아바타 수정"
              >
                {($auth.user?.name || $auth.user?.nickname || $auth.user?.email || '?')[0].toUpperCase()}
                <!-- Hover overlay -->
                <div class="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-200 flex items-center justify-center">
                  <svg class="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
                  </svg>
                </div>
              </button>
              <div class="absolute -bottom-1 -right-1 w-5 h-5 bg-success rounded-full border-2 border-white dark:border-dark-secondary"></div>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-h2 text-content truncate">
                {$auth.user?.name || $auth.user?.nickname || $auth.user?.email?.split('@')[0] || ''}
              </p>
              <p class="text-caption text-content-tertiary truncate mt-0.5">
                {$auth.user?.email}
              </p>
            </div>
          </div>

          {#if $auth.user?.organization}
            <a
              href="/settings#company-verification"
              class="block px-4 py-3 bg-surface-tertiary rounded-xl mb-4 hover:bg-gray-100 dark:hover:bg-dark-tertiary/80 transition-colors cursor-pointer"
            >
              <div class="flex items-center gap-2 text-sm">
                <svg class="w-4 h-4 text-content-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                </svg>
                <span class="text-content-secondary font-medium">{$auth.user.organization}</span>
                <svg class="w-3.5 h-3.5 text-content-tertiary ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </div>
            </a>
          {/if}

          <div class="grid grid-cols-2 gap-3">
            <a
              href="/messages"
              class="flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-50 dark:bg-primary/15 text-primary hover:bg-primary-100 dark:hover:bg-primary/25 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer group"
            >
              <svg class="w-4 h-4 transition-transform duration-200 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
              </svg>
              메시지
            </a>
            <a
              href="/notifications"
              class="flex items-center justify-center gap-2 px-4 py-2.5 bg-info-light dark:bg-info/15 text-info hover:bg-info-light/80 dark:hover:bg-info/25 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer group"
            >
              <svg class="w-4 h-4 transition-transform duration-200 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
              </svg>
              알림
            </a>
          </div>
        </div>

        <!-- Quick Actions Card -->
        <div class="card p-6">
          <h2 class="text-h2 text-content mb-4 flex items-center gap-2">
            <svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
            빠른 실행
          </h2>
          <div class="space-y-2">
            {#each quickActions as action}
              <a
                href={action.href}
                class="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-dark-tertiary transition-all duration-200 cursor-pointer group"
              >
                <div class="w-10 h-10 bg-{action.color}-50 dark:bg-{action.color}/10 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-200">
                  <svg class="w-5 h-5 text-{action.color}" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d={action.icon}/>
                  </svg>
                </div>
                <span class="text-sm font-medium text-gray-700 dark:text-dark-text-medium group-hover:text-gray-900 dark:group-hover:text-dark-text-high transition-colors duration-200">
                  {action.label}
                </span>
                <svg class="w-4 h-4 text-content-tertiary ml-auto group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </a>
            {/each}
          </div>
        </div>
      </div>

      <!-- Right Column: Stats & Activity -->
      <div class="lg:col-span-2 space-y-6">

        <!-- Stats Cards Grid -->
        <div class="grid sm:grid-cols-2 gap-4">
          {#each statCards as card}
            <div class="card p-6 transition-all duration-200 hover:shadow-lg hover:scale-[1.02] cursor-pointer group">
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <p class="text-sm font-medium text-content-secondary mb-2">
                    {card.label}
                  </p>
                  <p class="text-4xl font-bold text-content tabular-nums">
                    {stats[card.key].toLocaleString()}
                  </p>
                  {#if shouldShowChange(stats[card.changeKey])}
                    {@const change = stats[card.changeKey]!}
                    <div class="mt-3 flex items-center gap-1.5">
                      <div class="flex items-center gap-1 {change > 0 ? 'text-success' : 'text-error'}">
                        <svg class="w-4 h-4 {change < 0 ? 'rotate-180' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"/>
                        </svg>
                        <span class="text-sm font-semibold">{Math.abs(change)}%</span>
                      </div>
                      <span class="text-xs text-content-tertiary">이번 주</span>
                    </div>
                  {/if}
                </div>
                <div class="w-14 h-14 {card.bgClass} rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-200">
                  <svg class="w-7 h-7 {card.iconClass}" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d={card.icon}/>
                  </svg>
                </div>
              </div>
            </div>
          {/each}
        </div>

        <!-- Recent Activity Card -->
        <div class="card overflow-hidden">
          <div class="px-6 py-4 border-b border-default">
            <div class="flex items-center justify-between">
              <h2 class="text-h2 text-content flex items-center gap-2">
                <svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                최근 활동
              </h2>
              <a href="/boards" class="text-sm font-medium text-primary hover:text-primary-dark transition-colors duration-200 cursor-pointer">
                모두 보기 →
              </a>
            </div>
          </div>
          <div class="p-8 sm:p-12 lg:p-16">
            <div class="text-center max-w-md mx-auto">
              <div class="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-dark-tertiary dark:to-dark-elevated rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-inner">
                <svg class="w-10 h-10 text-content-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                </svg>
              </div>
              <h3 class="text-title text-content mb-2">
                아직 활동이 없습니다
              </h3>
              <p class="text-body2 text-content-secondary mb-6">
                게시글을 작성하거나 댓글을 남기면 여기에 표시됩니다
              </p>
              <a
                href="/boards"
                class="btn btn-primary btn-md group"
              >
                <svg class="w-5 h-5 group-hover:rotate-90 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                </svg>
                첫 게시글 작성하기
              </a>
            </div>
          </div>
        </div>

      </div>
    </div>
</div>

<!-- Avatar Edit Modal -->
{#if showAvatarModal}
  <div
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
    onclick={handleBackdropClick}
    onkeydown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        closeAvatarModal();
      }
    }}
    role="button"
    aria-label="아바타 수정 모달 닫기"
    tabindex="0"
  >
    <div class="card shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto animate-scale-in" role="dialog" aria-modal="true" aria-labelledby="avatar-modal-title" tabindex="0">
      <!-- Modal Header -->
      <div class="sticky top-0 bg-surface border-b border-default px-6 py-4 flex items-center justify-between">
        <h2 id="avatar-modal-title" class="text-h2 text-content">
          아바타 수정
        </h2>
        <button
          onclick={closeAvatarModal}
          class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-colors duration-200 cursor-pointer group"
          aria-label="모달 닫기"
        >
          <svg class="w-5 h-5 text-content-secondary group-hover:text-gray-700 dark:group-hover:text-dark-text-high" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- Modal Body -->
      <div class="p-6 space-y-6">
        <!-- Preview -->
        <div class="flex flex-col items-center">
          <p class="text-sm font-medium text-gray-700 dark:text-dark-text-medium mb-4">미리보기</p>
          {#if uploadedImage}
            <div class="w-24 h-24 rounded-2xl overflow-hidden shadow-lg">
              <img src={uploadedImage} alt="업로드된 아바타" class="w-full h-full object-cover" />
            </div>
          {:else}
            <div class="w-24 h-24 rounded-2xl bg-gradient-to-br {avatarColors.find(c => c.name === selectedAvatarColor)?.gradient || 'from-primary-400 to-primary-600'} flex items-center justify-center text-white text-4xl font-bold shadow-lg">
              {($auth.user?.name || $auth.user?.nickname || $auth.user?.email || '?')[0].toUpperCase()}
            </div>
          {/if}
        </div>

        <!-- Upload Image -->
        <div>
          <div class="block text-sm font-medium text-gray-700 dark:text-dark-text-medium mb-3">이미지 업로드</div>
          <div class="flex items-center gap-3">
            <label class="flex-1 cursor-pointer" for="dashboard-avatar-file">
              <input
                id="dashboard-avatar-file"
                type="file"
                accept="image/*"
                onchange={handleFileUpload}
                class="hidden"
              />
              <div class="flex items-center justify-center gap-2 px-4 py-3 bg-surface-tertiary hover:bg-gray-200 dark:hover:bg-dark-elevated border border-gray-300 dark:border-dark-border rounded-xl transition-colors duration-200 cursor-pointer">
                <svg class="w-5 h-5 text-content-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
                <span class="text-sm font-medium text-gray-700 dark:text-dark-text-medium">이미지 선택</span>
              </div>
            </label>
            {#if uploadedImage}
              <button
                onclick={() => { uploadedImage = null }}
                class="px-4 py-3 bg-error-light dark:bg-error/15 text-error hover:bg-error-light/80 dark:hover:bg-error/25 rounded-xl text-sm font-medium transition-colors duration-200 cursor-pointer"
                aria-label="업로드된 이미지 제거"
              >
                제거
              </button>
            {/if}
          </div>
          <p class="mt-2 text-xs text-content-tertiary">
            JPG, PNG, GIF 파일 (최대 5MB)
          </p>
        </div>

        <!-- Color Selection -->
        {#if !uploadedImage}
          <div>
            <div class="block text-sm font-medium text-gray-700 dark:text-dark-text-medium mb-3">아바타 색상 선택</div>
            <div class="grid grid-cols-3 gap-3">
              {#each avatarColors as color}
                <button
                  onclick={() => { selectedAvatarColor = color.name }}
                  class="relative group cursor-pointer"
                  aria-label={`${color.label} 선택`}
                >
                  <div class="w-full aspect-square rounded-xl bg-gradient-to-br {color.gradient} shadow-md transition-all duration-200 {selectedAvatarColor === color.name ? 'ring-4 ring-primary ring-offset-2 dark:ring-offset-dark-secondary scale-105' : 'hover:scale-105'}">
                    {#if selectedAvatarColor === color.name}
                      <div class="absolute inset-0 flex items-center justify-center">
                        <svg class="w-8 h-8 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                        </svg>
                      </div>
                    {/if}
                  </div>
                  <p class="mt-2 text-xs text-center font-medium text-content-secondary group-hover:text-gray-900 dark:group-hover:text-dark-text-high transition-colors duration-200">
                    {color.label}
                  </p>
                </button>
              {/each}
            </div>
          </div>
        {/if}
      </div>

      <!-- Modal Footer -->
      <div class="sticky bottom-0 bg-surface border-t border-default px-6 py-4 flex items-center justify-end gap-3">
        <button
          onclick={closeAvatarModal}
          class="btn btn-secondary btn-md"
        >
          취소
        </button>
        <button
          onclick={saveAvatar}
          class="btn btn-primary btn-md"
        >
          저장
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  /* Ensure smooth transitions respect reduced motion preferences */
  @media (prefers-reduced-motion: reduce) {
    * {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }

  /* Custom scrollbar for dark mode */
  :global(.dark) {
    scrollbar-color: #333333 #0d0d0d;
  }

  /* Tabular numbers for consistent stat alignment */
  .tabular-nums {
    font-feature-settings: "tnum";
    font-variant-numeric: tabular-nums;
  }
</style>
